import React from "react";
import { Shield, Mail, Info, Lock, UserCog, Bell, ExternalLink, BookOpen, Cookie, Globe, Clock, Edit } from "lucide-react";
import { Button } from "../components/ui/button";

export default function Privacy() {
  return (
    <>
      <section className="bg-background pt-32 pb-12">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <div className="flex flex-col items-center">
              <h1 className="text-4xl font-bold sm:text-5xl md:text-6xl bg-gradient-to-r from-yellow-500 to-amber-400 bg-clip-text text-transparent mb-3">
                Privacy Policy
              </h1>
              <div className="w-48 h-1 bg-gradient-to-r from-yellow-500 to-amber-400 rounded-full"></div>
            </div>
            <p className="text-lg text-muted-foreground">
              Last Updated: July 26, 2024
            </p>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="container">
          <div className="max-w-4xl mx-auto prose prose-slate">
            <div className="space-y-12">
              {/* Introduction Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Info className="h-6 w-6 text-yellow-500" />
                  Introduction
                </h2>
                <p className="text-muted-foreground">
                  Pagent Social Corp. and our affiliates and subsidiaries ("Company" or "We") respect your privacy and are committed to protecting it by complying with this policy.
                </p>
                <p className="text-muted-foreground mt-4">
                  This policy describes:
                </p>
                <ul className="list-disc pl-6 mt-4 space-y-2">
                  <li>How we collect, use, disclose, and protect the personal information of our customers and website users ("you").</li>
                  <li>The types of information we may collect from you or that you may provide when you visit the website Pagent.tv (our "Website").</li>
                  <li>Our practices for collecting, using, maintaining, protecting, and disclosing that information.</li>
                </ul>
                
                <div className="mt-6">
                  <p className="text-muted-foreground">
                    Privacy laws in Canada generally define "personal information" as any information about an identifiable individual, which includes information that can be used on its own or with other information to identify, contact, or locate a single person. Personal information does not include business contact information, including your name, title, or business contact information.
                  </p>
                  
                  <p className="text-muted-foreground mt-4">
                    This policy applies to information we collect, use, or disclose about you:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>On this Website.</li>
                    <li>In email, text, and other electronic messages between you and this Website.</li>
                    <li>Through mobile and desktop applications you download from this Website, which provide dedicated non-browser-based interaction between you and this Website.</li>
                    <li>When you interact with our advertising and applications on third-party websites and services if those applications or advertising include links to this policy.</li>
                  </ul>
                  
                  <p className="text-muted-foreground mt-4">
                    The Website may include links to third-party websites, plug-ins, services, social networks, or applications. Clicking on those links or enabling those connections may allow the third party to collect or share data about you. If you follow a link to a third-party website or engage a third-party plugin, please note that these third parties have their own privacy policies and we do not accept any responsibility or liability for these policies.
                  </p>
                  
                  <p className="text-muted-foreground mt-4">
                    Please read this policy carefully to understand our policies and practices for collecting, processing, and storing your information. If you do not agree with our policies and practices, your choice is not to use our Website. By accessing or using this Website, you indicate that you understand, accept, and consent to the practices described in this policy.
                  </p>
                </div>
              </div>

              {/* Information Collection Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Shield className="h-6 w-6 text-yellow-500" />
                  Information We Collect About You
                </h2>
                <p className="text-muted-foreground">
                  We collect several types of information from and about you, including:
                </p>
                <div className="mt-6 space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                      <UserCog className="h-5 w-5 text-yellow-500" />
                      Personal Information
                    </h3>
                    <p className="text-muted-foreground">
                      Information that we can reasonably use to directly or indirectly identify you, such as your name, mailing address, e-mail address, telephone number, Internet protocol (IP) address used to connect your computer to the Internet, user name or other similar identifier, and any other identifier we may use to contact you.
                    </p>
                    <p className="text-muted-foreground mt-2">
                      We provide an opportunity for any user to unsubscribe from our email list or opt-out of contact on an ongoing basis by accessing our Website, or using the unsubscribe mechanism at the bottom of our e-mails.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                      <Shield className="h-5 w-5 text-yellow-500" />
                      Non-personal Information
                    </h3>
                    <p className="text-muted-foreground">
                      Information that does not directly or indirectly reveal your identity or directly relate to an identifiable individual, such as demographic information, or statistical or aggregated information. Statistical or aggregated data does not directly identify a specific person, but we may derive non-personal statistical or aggregated data from personal information. For example, we may aggregate personal information to calculate the percentage of users accessing a specific Website feature.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                      <Globe className="h-5 w-5 text-yellow-500" />
                      Technical Information
                    </h3>
                    <p className="text-muted-foreground">
                      Including your login information, browser type and version, time zone setting, browser plug-in types and versions, operating system and platform, or information about your internet connection, the equipment you use to access our Website, and usage details.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                      <Bell className="h-5 w-5 text-yellow-500" />
                      Usage Details
                    </h3>
                    <p className="text-muted-foreground">
                      Non-personal details about your Website interactions, including the full Uniform Resource Locators (URLs), clickstream to, through and from our Website (including date and time), products you viewed or searched for, page response times, download errors, length of visits to certain pages, page interaction information (such as scrolling, clicks, and mouse-overs), methods used to browse away from the page, or any phone number used to call our customer service number.
                    </p>
                  </div>
                </div>
              </div>

              {/* How We Collect Information Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Info className="h-6 w-6 text-yellow-500" />
                  How We Collect Information About You
                </h2>
                <p className="text-muted-foreground">
                  We use different methods to collect your information, including through:
                </p>
                <ul className="list-disc pl-6 mt-4 space-y-2">
                  <li>Direct interactions with you when you provide it to us, for example, by filling in forms or corresponding with us by phone, email, or otherwise.</li>
                  <li>Automated technologies or interactions, as you navigate through our Website. Information collected automatically may include usage details, IP addresses, and information collected through cookies, and other tracking technologies.</li>
                  <li>Third parties or publicly available sources, for example, our business partners.</li>
                </ul>
                
                <h3 className="text-xl font-semibold mt-6 mb-3">Information You Provide to Us</h3>
                <p className="text-muted-foreground">
                  The information we collect directly from you on or through our Website may include:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Information that you provide by filling in forms on our Website</li>
                  <li>Records and copies of your correspondence (including email addresses), if you contact us</li>
                  <li>Your responses to surveys that we might ask you to complete for research purposes</li>
                  <li>Your search queries on the Website</li>
                </ul>
                
                <h3 className="text-xl font-semibold mt-6 mb-3 flex items-center gap-2">
                  <Cookie className="h-5 w-5 text-yellow-500" />
                  Information We Collect Through Cookies and Other Automatic Data Collection Technologies
                </h3>
                <p className="text-muted-foreground">
                  As you navigate through and interact with our Website, we may use cookies or other automatic data collection technologies to collect certain information about your equipment, browsing actions, and patterns, including:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Details of your visits to our Website, including traffic data, location data, logs, and other communication data and the resources that you access and use on the Website</li>
                  <li>Information about your computer and internet connection, including your IP address, operating system, and browser type</li>
                </ul>
                
                <p className="text-muted-foreground mt-4">
                  We may also use these technologies to collect information about your online activities over time and across third-party websites or other online services (behavioral tracking).
                </p>
                
                <div className="mt-6 bg-muted/30 p-4 rounded-md">
                  <h4 className="font-semibold mb-2">Technologies we use may include:</h4>
                  <ul className="space-y-3">
                    <li>
                      <span className="font-medium">Cookies (browser cookies)</span>: A small file placed on the hard drive of your computer. You may refuse to accept browser cookies by activating the appropriate setting on your browser.
                    </li>
                    <li>
                      <span className="font-medium">Flash Cookies</span>: Certain features of our Website may use local stored objects to collect and store information about your preferences.
                    </li>
                    <li>
                      <span className="font-medium">Web Beacons</span>: Small electronic files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit the Company to count users and for other related website statistics.
                    </li>
                  </ul>
                </div>
              </div>

              {/* Data Usage Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <UserCog className="h-6 w-6 text-yellow-500" />
                  How We Use Your Information
                </h2>
                <p className="text-muted-foreground mb-4">
                  We use information that we collect about you or that you provide to us, including any personal information:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>To present our Website and its contents to you</li>
                  <li>To provide you with information, products, or services that you request from us</li>
                  <li>To fulfill the purposes for which you provided the information or that were described when it was collected</li>
                  <li>To carry out our obligations and enforce our rights arising from any contracts with you</li>
                  <li>To notify you about changes to our Website or any products or services we offer</li>
                  <li>To improve our Website, products or services, marketing, or customer relationships</li>
                  <li>To allow you to participate in interactive features, social media, or similar features on our Website</li>
                  <li>To measure or understand the effectiveness of the advertising we serve to you and others</li>
                  <li>In any other way we may describe when you provide the information</li>
                  <li>For any other purpose with your consent</li>
                </ul>
              </div>

              {/* Disclosure Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <ExternalLink className="h-6 w-6 text-yellow-500" />
                  Disclosure of Your Information
                </h2>
                <p className="text-muted-foreground">
                  We may disclose aggregated information about our users, and information that does not identify any individual, without restriction.
                </p>
                
                <p className="text-muted-foreground mt-4">
                  We may disclose personal information that we collect or you provide as described in this privacy policy:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>To our subsidiaries and affiliates</li>
                  <li>In accordance with applicable law, to a buyer or other successor in the event of a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Pagent Social Corp's assets</li>
                  <li>To advertisers and advertising networks that require the information to select and serve relevant advertisements to you and others</li>
                  <li>To third parties to market their products or services to you if you have not opted out of these disclosures</li>
                  <li>To contractors, service providers, and other third parties we use to support our business</li>
                  <li>For any other purpose disclosed by us when you provide the information</li>
                  <li>With your consent</li>
                </ul>
                
                <p className="text-muted-foreground mt-4">
                  We may also disclose your personal information:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>To comply with any court order, law, or legal process, including to respond to any government or regulatory request</li>
                  <li>To enforce or apply our terms of use and other agreements, including for billing and collection purposes</li>
                  <li>If we believe disclosure is necessary or appropriate to protect the rights, property, or safety of Pagent Social Corp., our customers, or others</li>
                </ul>
              </div>

              {/* Transferring Information Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Globe className="h-6 w-6 text-yellow-500" />
                  Transferring Your Personal Information
                </h2>
                <p className="text-muted-foreground">
                  We may transfer personal information that we collect or that you provide as described in this policy to contractors, service providers, and other third parties we use to support our business and who are contractually obligated to keep personal information confidential, use it only for the purposes for which we disclose it to them, and to process the personal information with the same standards set out in this policy.
                </p>
                
                <p className="text-muted-foreground mt-4">
                  We may process, store, and transfer your personal information in and to a foreign country, with different privacy laws that may or may not be as comprehensive as Canadian law. In these circumstances, the governments, courts, law enforcement, or regulatory agencies of that country may be able to obtain access to your personal information through the laws of the foreign country.
                </p>
                
                <p className="text-muted-foreground mt-4">
                  By submitting your personal information or engaging with the Website, you consent to this transfer, storage, or processing.
                </p>
              </div>

              {/* Data Security Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Lock className="h-6 w-6 text-yellow-500" />
                  Data Security
                </h2>
                <p className="text-muted-foreground">
                  The security of your personal information is very important to us. We use physical, electronic, and administrative measures designed to secure your personal information from accidental loss and from unauthorized access, use, alteration, and disclosure.
                </p>
                
                <p className="text-muted-foreground mt-4">
                  The safety and security of your information also depends on you. Where we have given you (or where you have chosen) a password for access to certain parts of our Website, you are responsible for keeping this password confidential. We ask you not to share your password with anyone.
                </p>
                
                <p className="text-muted-foreground mt-4">
                  Unfortunately, the transmission of information via the Internet is not completely secure. Although we do our best to protect your personal information, we cannot guarantee the security of your personal information transmitted to our Website. Any transmission of personal information is at your own risk.
                </p>
              </div>

              {/* Data Retention Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Clock className="h-6 w-6 text-yellow-500" />
                  Data Retention
                </h2>
                <p className="text-muted-foreground">
                  Except as otherwise permitted or required by applicable law or regulation, we will only retain your personal information for as long as necessary to fulfill the purposes we collected it for, including for the purposes of satisfying any legal, accounting, or reporting requirements.
                </p>
                
                <p className="text-muted-foreground mt-4">
                  Under some circumstances we may anonymize your personal information so that it can no longer be associated with you. We reserve the right to use such anonymous and de-identified data for any legitimate business purpose without further notice to you or your consent.
                </p>
              </div>

              {/* Accessing and Correcting Information Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Edit className="h-6 w-6 text-yellow-500" />
                  Accessing and Correcting Your Personal Information
                </h2>
                <p className="text-muted-foreground">
                  It is important that the personal information we hold about you is accurate and current. Please keep us informed if your personal information changes. By law you have the right to request access to and to correct the personal information that we hold about you.
                </p>
                
                <p className="text-muted-foreground mt-4">
                  If you want to review, verify, correct, or withdraw consent to the use of your personal information you may also send us an email using the "Contact Us" feature to request access to, correct, or delete any personal information that you have provided to us.
                </p>
                
                <p className="text-muted-foreground mt-4">
                  We may request specific information from you to help us confirm your identity and your right to access, and to provide you with the personal information that we hold about you or make your requested changes.
                </p>
              </div>

              {/* Consent Withdrawal Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Bell className="h-6 w-6 text-yellow-500" />
                  Withdrawing Your Consent
                </h2>
                <p className="text-muted-foreground">
                  Where you have provided your consent to the collection, use, and transfer of your personal information, you may have the legal right to withdraw your consent under certain circumstances. To withdraw your consent, if applicable, contact us at the "Contact Us" page.
                </p>
                
                <p className="text-muted-foreground mt-4">
                  Please note that if you withdraw your consent we may not be able to provide you with a particular product or service. We will explain the impact to you at the time to help you with your decision.
                </p>
              </div>

              {/* Updates Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <BookOpen className="h-6 w-6 text-yellow-500" />
                  Changes to Our Privacy Policy
                </h2>
                <p className="text-muted-foreground">
                  It is our policy to post any changes we make to our privacy policy on this page. If we make material changes to how we treat our users' personal information, we will notify you through a notice on the Website home page.
                </p>
                
                <p className="text-muted-foreground mt-4">
                  We include the date the privacy policy was last revised at the top of the page. You are responsible for ensuring we have an up-to-date, active, and deliverable email address for you, and for periodically visiting our Website and this privacy policy to check for any changes.
                </p>
              </div>

              {/* Contact Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Mail className="h-6 w-6 text-yellow-500" />
                  Contact Us
                </h2>
                <p className="text-muted-foreground mb-6">
                  If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button variant="outline" className="gap-2">
                    <Mail className="h-4 w-4 text-yellow-500" />
                    privacy@pagent.com
                  </Button>
                  <Button variant="outline" className="gap-2">
                    <ExternalLink className="h-4 w-4 text-yellow-500" />
                    Visit Help Center
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground mt-6">
                  We will respond to your request within a reasonable timeframe.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}